// src/components/LoyaltyProgramSteps/Step2EarningRules.js
import React from 'react';
import { CalculatorIcon, TagIcon } from '@heroicons/react/24/outline';

const Step2EarningRules = ({ formData, updateFormData }) => {
    const renderEarningRulesByProgramType = () => {
        switch (formData.programType) {
            case 'points':
                return (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="points-per-spend" className="block text-sm font-medium text-gray-700">Points Earned</label>
                                <div className="relative mt-1">
                                    <input
                                        id="points-per-spend"
                                        name="points-per-spend"
                                        type="number"
                                        min="1"
                                        required
                                        className="block w-full pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        placeholder="e.g., 1"
                                        value={formData.pointsPerSpend}
                                        onChange={(e) => updateFormData('pointsPerSpend', e.target.value)}
                                    />
                                    <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 text-sm">points</span>
                                </div>
                            </div>
                            <div>
                                <label htmlFor="spend-amount" className="block text-sm font-medium text-gray-700">for every Rands Spent</label>
                                <div className="relative mt-1">
                                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 text-sm">R</span>
                                    <input
                                        id="spend-amount"
                                        name="spend-amount"
                                        type="number"
                                        min="1"
                                        step="0.01"
                                        required
                                        className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        placeholder="e.g., 10"
                                        value={formData.spendAmount}
                                        onChange={(e) => updateFormData('spendAmount', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">Example: Earn {formData.pointsPerSpend || 'X'} points for every R{formData.spendAmount || 'Y'} spent.</p>
                    </>
                );
            case 'tier':
                return (
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                        <p className="text-sm text-blue-700">
                            Tier-based earning rules are set up within the 'Tier Levels' section. You'll define criteria like total spend or points to reach each tier. This functionality would be configured in a dedicated sub-section.
                        </p>
                    </div>
                );
            case 'stamp':
                return (
                    <div>
                        <label htmlFor="stamps-needed" className="block text-sm font-medium text-gray-700">Stamps Needed for Reward</label>
                        <input
                            id="stamps-needed"
                            name="stamps-needed"
                            type="number"
                            min="1"
                            required
                            className="mt-1 block w-full pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="e.g., 5"
                            value={formData.stampsNeeded || ''}
                            onChange={(e) => updateFormData('stampsNeeded', e.target.value)}
                        />
                        <p className="text-sm text-gray-500 mt-2">
                            Define what constitutes a "stamp" (e.g., "1 stamp per coffee purchase").
                        </p>
                    </div>
                );
            case 'spend':
                return (
                    <div>
                        <label htmlFor="spend-threshold" className="block text-sm font-medium text-gray-700">Spend Threshold for Reward</label>
                        <div className="relative mt-1">
                            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 text-sm">R</span>
                            <input
                                id="spend-threshold"
                                name="spend-threshold"
                                type="number"
                                min="1"
                                step="0.01"
                                required
                                className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                placeholder="e.g., 500"
                                value={formData.spendThreshold || ''}
                                onChange={(e) => updateFormData('spendThreshold', e.target.value)}
                            />
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                            Customers will receive a reward once their total accumulated spend reaches this amount.
                        </p>
                    </div>
                );
            default:
                return <p className="text-gray-600">Please select a Program Type in Step 1 to configure earning rules.</p>;
        }
    };

    return (
        <fieldset className="border p-4 rounded-md space-y-4">
            <legend className="text-lg font-medium text-gray-900 flex items-center">
                <CalculatorIcon className="h-6 w-6 text-indigo-500 mr-2" />
                Step 2: Earning Rules
            </legend>

            {renderEarningRulesByProgramType()}

            {/* Earning Exclusions (General for all types) */}
            <div>
                <label htmlFor="earning-exclusions" className="block text-sm font-medium text-gray-700">Earning Exclusions (Optional)</label>
                <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 pt-2 flex items-start pointer-events-none">
                        <TagIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <textarea
                        id="earning-exclusions"
                        name="earning-exclusions"
                        rows="2"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="e.g., No points on discounted items, gift card purchases"
                        value={formData.earningExclusions}
                        onChange={(e) => updateFormData('earningExclusions', e.target.value)}
                    ></textarea>
                </div>
            </div>
        </fieldset>
    );
};

export default Step2EarningRules;